// Synth renderer — recipe in, AudioBuffer out, via OfflineAudioContext.
//
// Graph:  Oscillator → BiquadFilter (LPF) → Gain (ADSR) → destination
//
// The ADSR gain envelope is hand-plotted via linearRampToValueAtTime so
// the entire curve is deterministic across browsers. The filter gets a
// matching envelope scaled by filterEnvAmount — classic subtractive
// shape where the cutoff sweeps down as the note decays.
//
// Pure function; live-audition calls are in the verb component.

import type { SynthRecipe } from '../../types';

export const DEFAULT_SYNTH_RECIPE: SynthRecipe = {
  osc: 'sawtooth',
  detuneCents: 0,
  pitchMidi: 48, // C3
  attackMs: 4,
  decayMs: 120,
  sustain: 0.0,
  releaseMs: 60,
  filterHz: 2000,
  filterEnvAmount: 0.6,
  lengthMs: 300,
};

export function midiToHz(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

export async function renderSynth(
  recipe: SynthRecipe,
  sampleRate: number = 48000,
): Promise<AudioBuffer> {
  const frames = Math.max(1, Math.round((recipe.lengthMs / 1000) * sampleRate));
  const ctx = new OfflineAudioContext(2, frames, sampleRate);
  buildSynthGraph(ctx, recipe, ctx.destination);
  const rendered = await ctx.startRendering();
  return rendered;
}

// Shared graph builder so live audition (on a live AudioContext) uses
// the same signal chain as the offline render — audition == commit.
export function buildSynthGraph(
  ctx: BaseAudioContext,
  recipe: SynthRecipe,
  destination: AudioNode,
): void {
  const now = ctx.currentTime;
  const freq = midiToHz(recipe.pitchMidi);
  const lengthS = recipe.lengthMs / 1000;
  const attackS = Math.max(0.001, recipe.attackMs / 1000);
  const decayS = Math.max(0.001, recipe.decayMs / 1000);
  const releaseS = Math.max(0.001, recipe.releaseMs / 1000);
  const sustainLevel = clamp01(recipe.sustain);

  const osc = ctx.createOscillator();
  osc.type = recipe.osc;
  osc.frequency.value = freq;
  osc.detune.value = recipe.detuneCents;

  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.Q.value = 0.7;
  // Filter envelope: starts at the base cutoff boosted by envAmount
  // octaves, decays back to the base during the decay stage.
  const envOctaves = 4 * clamp01(recipe.filterEnvAmount);
  const cutoffPeak = recipe.filterHz * Math.pow(2, envOctaves);
  filter.frequency.setValueAtTime(recipe.filterHz, now);
  filter.frequency.linearRampToValueAtTime(cutoffPeak, now + attackS);
  filter.frequency.linearRampToValueAtTime(
    recipe.filterHz,
    now + attackS + decayS,
  );

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(1, now + attackS);
  gain.gain.linearRampToValueAtTime(sustainLevel, now + attackS + decayS);
  // Hold sustain until (lengthS - releaseS), then ramp to 0.
  const sustainEnd = Math.max(
    now + attackS + decayS,
    now + lengthS - releaseS,
  );
  gain.gain.setValueAtTime(sustainLevel, sustainEnd);
  gain.gain.linearRampToValueAtTime(0, now + lengthS);

  osc.connect(filter);
  filter.connect(gain);
  gain.connect(destination);
  osc.start(now);
  osc.stop(now + lengthS);
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

// Seeded randomness for Roll. Supports constraint "tone classes" so the
// roll stays musically plausible instead of hitting the unlistenable
// corners of the parameter space.

export type ToneClass = 'kick' | 'snare' | 'perc' | 'bass' | 'lead';

type Range = [number, number];

type RollProfile = {
  osc: SynthRecipe['osc'][];
  pitchMidi: Range;
  detuneCents: Range;
  attackMs: Range;
  decayMs: Range;
  sustain: Range;
  releaseMs: Range;
  filterHz: Range;
  filterEnvAmount: Range;
  lengthMs: Range;
};

const PROFILES: Record<ToneClass, RollProfile> = {
  kick: {
    osc: ['sine', 'triangle'],
    pitchMidi: [24, 40],
    detuneCents: [-5, 5],
    attackMs: [1, 5],
    decayMs: [80, 200],
    sustain: [0, 0.1],
    releaseMs: [40, 120],
    filterHz: [500, 3000],
    filterEnvAmount: [0.3, 0.9],
    lengthMs: [200, 500],
  },
  snare: {
    osc: ['sawtooth', 'square'],
    pitchMidi: [55, 75],
    detuneCents: [-20, 20],
    attackMs: [1, 3],
    decayMs: [40, 120],
    sustain: [0, 0.1],
    releaseMs: [20, 80],
    filterHz: [1500, 6000],
    filterEnvAmount: [0.2, 0.6],
    lengthMs: [120, 300],
  },
  perc: {
    osc: ['triangle', 'square'],
    pitchMidi: [60, 90],
    detuneCents: [-50, 50],
    attackMs: [0.5, 2],
    decayMs: [20, 80],
    sustain: [0, 0.05],
    releaseMs: [10, 40],
    filterHz: [2000, 8000],
    filterEnvAmount: [0.2, 0.8],
    lengthMs: [80, 200],
  },
  bass: {
    osc: ['sawtooth', 'square', 'triangle'],
    pitchMidi: [28, 48],
    detuneCents: [-10, 10],
    attackMs: [2, 10],
    decayMs: [80, 250],
    sustain: [0.3, 0.7],
    releaseMs: [60, 200],
    filterHz: [300, 1500],
    filterEnvAmount: [0.2, 0.6],
    lengthMs: [400, 1200],
  },
  lead: {
    osc: ['sawtooth', 'square'],
    pitchMidi: [60, 84],
    detuneCents: [-15, 15],
    attackMs: [2, 10],
    decayMs: [100, 400],
    sustain: [0.3, 0.7],
    releaseMs: [100, 300],
    filterHz: [1500, 6000],
    filterEnvAmount: [0.3, 0.7],
    lengthMs: [500, 1500],
  },
};

function mulberry32(seed: number): () => number {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = seed;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function rollRecipe(toneClass: ToneClass, seed?: number): SynthRecipe {
  const r = mulberry32(seed ?? Math.floor(Math.random() * 1e9));
  const p = PROFILES[toneClass];
  const pick = <T>(arr: T[]): T => arr[Math.floor(r() * arr.length)]!;
  const lerp = (range: Range) => range[0] + r() * (range[1] - range[0]);
  return {
    osc: pick(p.osc),
    detuneCents: lerp(p.detuneCents),
    pitchMidi: Math.round(lerp(p.pitchMidi)),
    attackMs: lerp(p.attackMs),
    decayMs: lerp(p.decayMs),
    sustain: lerp(p.sustain),
    releaseMs: lerp(p.releaseMs),
    filterHz: lerp(p.filterHz),
    filterEnvAmount: lerp(p.filterEnvAmount),
    lengthMs: lerp(p.lengthMs),
  };
}
